import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { LinksService } from '@app/core/links.service';
import { StorageService } from '@app/core/storage.service';
import { OmguiButtonSize, OmguiButtonType, OmguiButtonVariant } from '@omgui/omgui-button/omgui-button.component';

import { OtpVerificationService } from './otp-verification.service';

@Component({
  selector: 'om-otp-verification',
  templateUrl: './otp-verification.component.html',
  styleUrls: ['./otp-verification.component.scss'],
})
export class OtpVerificationComponent implements OnInit {
  otpForm: FormGroup;
  isSubmitting = false;
  phoneNumber = '';
  otpDigits: string[] = ['', '', '', '', '', ''];
  errorMessage = '';

  protected readonly OmguiButtonVariant = OmguiButtonVariant;
  protected readonly OmguiButtonSize = OmguiButtonSize;
  protected readonly OmguiButtonType = OmguiButtonType;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private otpVerificationService: OtpVerificationService,
    readonly links: LinksService,
    private storageService: StorageService,
  ) {}

  ngOnInit(): void {
    const rawPhoneNumber = history.state?.phoneNumber || '';
    this.phoneNumber = this.maskPhoneNumber(rawPhoneNumber);
    this.otpForm = this.formBuilder.group({
      code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    });
  }

  private maskPhoneNumber(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    return digits ? `XXX-XXX-${digits}` : '';
  }

  onKeyPress(event: KeyboardEvent, index: number): void {
    const char = event.key;

    if (!/^[0-9]$/.test(char)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const input = event.target as HTMLInputElement;
    input.value = char;
    this.otpDigits[index] = char;
    this.updateFormValue();

    // Move to next input
    if (index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement;
      nextInput?.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      const input = event.target as HTMLInputElement;
      this.otpDigits[index] = '';
      input.value = '';
      this.updateFormValue();

      // Move to previous input
      if (index > 0) {
        const prevInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement;
        prevInput?.focus();
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');

    for (let i = 0; i < 6; i++) {
      this.otpDigits[i] = digits[i] || '';
      const input = document.getElementById(`otp-${i}`) as HTMLInputElement;
      if (input) {
        input.value = this.otpDigits[i];
      }
    }

    this.updateFormValue();
  }

  private updateFormValue(): void {
    const code = this.otpDigits.join('');
    this.otpForm.patchValue({ code });
  }

  onSubmit(): void {
    if (this.otpForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.errorMessage = '';

      const token = this.storageService.getItem('verificationToken');
      const code = this.otpForm.value.code;

      if (!token) {
        this.router.navigate(['/forgot_password/password_reset_email_sent']);
        return;
      }

      this.otpVerificationService
        .mutate({
          input: {
            verificationToken: token,
            otpCode: code,
          },
        })
        .subscribe({
          next: result => {
            const response = result.data?.verifyOtpCode;
            if (response?.status === 'verified') {
              this.router.navigate(['/forgot_password/password_reset_email_sent']);
            } else {
              switch (response?.status) {
                case 'invalid_code':
                  this.errorMessage = 'Incorrect code. Please try again.';
                  break;
                case 'expired':
                  this.errorMessage = 'Code has expired. Please request a new one.';
                  break;
                default:
                  this.errorMessage = 'Something went wrong. Please try again.';
              }
              this.clearOtpInputs();
            }
            this.isSubmitting = false;
          },
          error: () => {
            this.errorMessage = 'Incorrect code. Please try again.';
            this.clearOtpInputs();
            this.isSubmitting = false;
          },
        });
    }
  }

  private clearOtpInputs(): void {
    this.otpDigits = ['', '', '', '', '', ''];
    for (let i = 0; i < 6; i++) {
      const input = document.getElementById(`otp-${i}`) as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    }
    this.otpForm.patchValue({ code: '' });
    const firstInput = document.getElementById('otp-0') as HTMLInputElement;
    firstInput?.focus();
  }

  onBack(): void {
    this.router.navigate(['/forgot_password/identity_verification']);
  }
}
